import { BehaviorSubject, Subject } from 'rxjs';

import { Platform } from '@angular/cdk/platform';

import { FsDataDogRumService } from '@lib/data-dog-rum';
import { SnPlayerState } from '@lib/player/common';

import { SnVideoPlayerType } from '../video-player.enums';
import { SnVideoPlayerReference } from '../video-player.reference';
import { PLAYERS_REFERENCE } from './players.reference.constants';
import { emitStreamError } from './stream-error.utils';

export class SnHtml5PlayerReference extends SnVideoPlayerReference {
  private _playPromise: Promise<void>;

  private _callback: Function;

  private _isPlaying: boolean;

  private _src: string;

  constructor(
    playerElement: HTMLElement,
    videoElement: HTMLVideoElement,
    src: string,
    platform: Platform,
    state$: BehaviorSubject<SnPlayerState>,
    currentTime$: BehaviorSubject<number>,
    bufferTime$: BehaviorSubject<number>,
    totalTime$: BehaviorSubject<number>,
    volume$: BehaviorSubject<number>,
    played$: BehaviorSubject<null>,
    loaded$: Subject<boolean>,
    ended$: Subject<void>,
    autoplay: boolean,
    callback: Function,
    private readonly _datadogService: FsDataDogRumService,
    private readonly camId: string,
  ) {
    super(
      SnVideoPlayerType.HTML5,
      playerElement,
      platform,
      state$,
      currentTime$,
      bufferTime$,
      totalTime$,
      volume$,
      played$,
      loaded$,
      ended$,
    );
    this._callback = callback;
    this._src = src;
    this._init(videoElement, src, autoplay);
  }

  public play(): void {
    this._isPlaying = true;
    this._playPromise = this.videoElement.play();
  }

  public pause(): void {
    this._isPlaying = false;
    const playPromise = this._playPromise;
    if (playPromise !== undefined) {
      playPromise.then(() => {
        this.videoElement.pause();
        if (this._playPromise === playPromise) {
          this._playPromise = undefined;
        }
      });
    } else {
      this.videoElement.pause();
    }
  }

  public destroy(): void {
    this.videoElement.pause();
    this.videoElement.removeAttribute('src');
    this.videoElement.load();
  }

  private _init(element: HTMLVideoElement, src: string, autoplay: boolean): void {
    this.videoElement = element;
    this.videoElement.src = src;
    this.videoElement.preload = 'none';
    this.videoElement.onerror = () => {
      console.error(this.videoElement.error);
      const errorCode = this.videoElement.error?.code;
      const isFatal = errorCode === 2 || errorCode === 3 || errorCode === 4;
      emitStreamError({
        datadogService: this._datadogService,
        cameraId: this.camId,
        protocol: PLAYERS_REFERENCE.PROTOCOL.HTML5,
        sourceUi: PLAYERS_REFERENCE.SOURCE_UI.HTML5,
        errorType: 'video_element_error',
        rawError: this.videoElement.error?.message || 'HTML5 video element error',
        errorCode: errorCode || 'UNKNOWN',
        isLive: this.live,
        isFatal,
        streamUrl: this._src,
        protocolSpecificFields: {
          media_error_code: typeof errorCode === 'number' ? errorCode : undefined,
          media_error_message: this.videoElement.error?.message || 'HTML5 video element error',
        },
      });
      if (errorCode !== 4) {
        this.videoElement.src = src;
        this.videoElement.play();
      }
    };
    if (autoplay || this._isPlaying) {
      this._playPromise = this.videoElement.play();
      this._playPromise.then(() => this._callback());
    } else {
      this._callback();
    }
  }
}
